// Chemin: C:\gestion-copro\src\pages\admin\CourrierDetailPage.tsx
// Session 42 : création initiale
// Session 43 : intégration analyse SCUD avant soumission
// Session 47 : génération PDF pour courriers postaux
// Session 75 : Sécurité annulation (bouton visible uniquement pour Gestionnaire/Admin SaaS)
// Session 80 : Ajout coproprieteId à analyserCourrierSCUD pour gestion quota SENTINELLE
// Session 92 : Classification 3 niveaux (VERT/ORANGE/ROUGE) - Gouvernance V2

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Send,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Paperclip,
  Download,
  MessageSquare,
  Copy,
  X,
  Shield,
  ThumbsUp,
  ThumbsDown,
  PenLine,
  Loader2,
  Mail,
  MapPin,
  Building2,
  Calendar,
  User,
  AlertCircle,
  Lightbulb,
  ShieldAlert,
  ShieldCheck,
  Printer,
  ArrowUpCircle
} from 'lucide-react';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  getCourrierById,
  soumettreCourrier,
  voterCourrier,
  annulerCourrier,
  dupliquerCourrier,
  marquerCommeEnvoye,
  analyserCourrierSCUD,
  sauvegarderAnalyseSCUD,
  surclasserNiveau
} from '../../services/courrier.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
// Session 47 : Import service copropriété et PDF
import { getCopropriete } from '../../services/copropriete.service';
import { telechargerPDFCourrier } from '../../services/pdfCourrier.service';
import { Copropriete } from '../../types/copropriete.types';
import {
  Courrier,
  StatutVote,
  AnalyseSCUD,
  AlerteSCUD,
  NiveauClassification,
  STATUT_COURRIER_LABELS,
  STATUT_COURRIER_COLORS,
  TYPE_COURRIER_LABELS,
  MODE_ENVOI_LABELS,
  STATUT_VOTE_LABELS,
  STATUT_VOTE_COLORS,
  NIVEAU_RISQUE_LABELS,
  NIVEAU_RISQUE_COLORS,
  NIVEAU_CLASSIFICATION_LABELS,
  NIVEAU_CLASSIFICATION_SHORT_LABELS,
  NIVEAU_CLASSIFICATION_COLORS,
  NIVEAU_CLASSIFICATION_DOT_COLORS,
  NIVEAU_CLASSIFICATION_DESCRIPTIONS,
  peutModifier,
  peutSoumettre,
  peutEnvoyer,
  aVote,
  compterVotes,
  necessiteVoteCS,
  getNiveauxSurclassementPossibles
} from '../../types/courrier.types';
import { MembreCS } from '../../types/conseilSyndical.types';

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const CourrierDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { coproprieteId } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();

  // Session 47 : Ref pour éviter double déclenchement de l'analyse
  const soumissionDeclenchee = useRef(false);

  // États
  const [courrier, setCourrier] = useState<Courrier | null>(null);
  const [membresCS, setMembresCS] = useState<MembreCS[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionEnCours, setActionEnCours] = useState<string | null>(null);

  // Session 47 : État pour la copropriété (nécessaire pour le PDF)
  const [copropriete, setCopropriete] = useState<Copropriete | null>(null);

  // Modale de vote
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [voteStatut, setVoteStatut] = useState<StatutVote>('approuve');
  const [voteCommentaire, setVoteCommentaire] = useState('');
  const [voteEnCours, setVoteEnCours] = useState(false);

  // Modale analyse SCUD
  const [showAnalyseModal, setShowAnalyseModal] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [analyseResultat, setAnalyseResultat] = useState<AnalyseSCUD | null>(null);

  // Dériver si l'utilisateur est membre CS
  const userId = firebaseUser?.uid;
  const userNom = userProfile?.nom 
    ? `${userProfile.prenom || ''} ${userProfile.nom}`.trim() 
    : userProfile?.email || 'Utilisateur';
  const estMembreCS = membresCS.some(m => m.userId === userId);
  const aDejaVote = courrier && userId ? aVote(courrier, userId) : false;

  // Session 75 : Helper pour la permission d'annulation
  const canAnnulerCourrier = userProfile?.isGestionnaireSaas === true || 
                             userProfile?.role === 'gestionnaire' || 
                             userProfile?.role === 'admin_saas';

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================

  useEffect(() => {
    const chargerDonnees = async () => {
      if (!id || !coproprieteId) return;

      setLoading(true);
      setError(null);

      try {
        // Session 47 : Charger aussi la copropriété pour le PDF
        const [courrierData, membresData, coproprieteData] = await Promise.all([
          getCourrierById(id),
          getMembresCSByCopropriete(coproprieteId, true),
          getCopropriete(coproprieteId)
        ]);

        if (!courrierData) {
          setError('Courrier non trouvé');
          return;
        }

        setCourrier(courrierData);
        setMembresCS(membresData);
        setCopropriete(coproprieteData);
      } catch (err) {
        console.error('Erreur chargement courrier:', err);
        setError('Impossible de charger le courrier');
      } finally {
        setLoading(false);
      }
    };

    chargerDonnees();
  }, [id, coproprieteId]);

  // Session 47 : Déclenchement automatique de la soumission si ?action=soumettre
  useEffect(() => {
    const action = searchParams.get('action');
    if (
      action === 'soumettre' && 
      courrier && 
      courrier.statut === 'brouillon' && 
      userId && 
      !soumissionDeclenchee.current &&
      !analyseEnCours
    ) {
      soumissionDeclenchee.current = true;
      // Supprimer le paramètre de l'URL
      setSearchParams({});
      // Déclencher la soumission
      handleSoumettre();
    }
  }, [searchParams, courrier, userId, analyseEnCours]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Lance l'analyse SCUD avant soumission
   */
  const handleSoumettre = async () => {
    if (!courrier || !userId) return;

    // Lancer l'analyse SCUD
    setAnalyseEnCours(true);
    setShowAnalyseModal(true);
    setAnalyseResultat(null);

    try {
      // Session 80 : Ajout coproprieteId pour gestion quota SENTINELLE
      const analyse = await analyserCourrierSCUD(
        coproprieteId!,
        courrier.objet,
        courrier.contenu,
        courrier.destinataire.nom,
        TYPE_COURRIER_LABELS[courrier.type]
      );
      setAnalyseResultat(analyse);

      // Si risque faible et aucune alerte, soumettre directement
      if (analyse.scoreRisque === 'faible' && analyse.alertes.length === 0) {
        await finaliserSoumission(analyse);
      }
      // Sinon, la modale reste ouverte pour que l'utilisateur décide
    } catch (err) {
      console.error('Erreur analyse SCUD:', err);
      setError('Erreur lors de l\'analyse du courrier');
      setShowAnalyseModal(false);
    } finally {
      setAnalyseEnCours(false);
    }
  };

  /**
   * Finalise la soumission après analyse
   */
  const finaliserSoumission = async (analyse: AnalyseSCUD) => {
    if (!courrier || !userId) return;

    setActionEnCours('soumettre');
    try {
      // Sauvegarder l'analyse
      await sauvegarderAnalyseSCUD(courrier.id, analyse);
      
      // Soumettre le courrier
      await soumettreCourrier(courrier.id, userId, userNom);
      
      // Recharger les données
      const updated = await getCourrierById(courrier.id);
      setCourrier(updated);
      setShowAnalyseModal(false);
    } catch (err) {
      console.error('Erreur soumission:', err);
      setError('Erreur lors de la soumission');
    } finally {
      setActionEnCours(null);
    }
  };

  /**
   * Soumettre malgré les alertes
   */
  const handleSoumettreQuandMeme = async () => {
    if (analyseResultat) {
      await finaliserSoumission(analyseResultat);
    }
  };

  /**
   * Fermer la modale et modifier le courrier
   */
  const handleModifierCourrier = () => {
    setShowAnalyseModal(false);
    navigate(`/admin/courriers/${courrier?.id}/modifier`);
  };

  const handleAnnuler = async () => {
    if (!courrier || !userId) return;

    const motif = window.prompt('Motif d\'annulation (optionnel) :');
    if (motif === null) return; // Annulé

    setActionEnCours('annuler');
    try {
      await annulerCourrier(courrier.id, userId, userNom, motif || undefined);
      const updated = await getCourrierById(courrier.id);
      setCourrier(updated);
    } catch (err) {
      console.error('Erreur annulation:', err);
      setError('Erreur lors de l\'annulation');
    } finally {
      setActionEnCours(null);
    }
  };

  const handleDupliquer = async () => {
    if (!courrier || !userId) return;

    const confirmation = window.confirm(
      'Créer une nouvelle version de ce courrier ?\n\nUne copie en brouillon sera créée avec le même contenu.'
    );
    if (!confirmation) return;

    setActionEnCours('dupliquer');
    try {
      const newId = await dupliquerCourrier(courrier.id, userId, userNom);
      navigate(`/admin/courriers/${newId}`);
    } catch (err) {
      console.error('Erreur duplication:', err);
      setError('Erreur lors de la duplication');
    } finally {
      setActionEnCours(null);
    }
  };

  const handleEnvoyer = async () => {
    if (!courrier || !userId) return;

    // Pour l'instant, on marque comme envoyé manuellement
    // TODO: Intégrer l'envoi Gmail réel
    const confirmation = window.confirm(
      'Marquer ce courrier comme envoyé ?\n\nCette action est définitive.'
    );
    if (!confirmation) return;

    setActionEnCours('envoyer');
    try {
      await marquerCommeEnvoye(
        courrier.id,
        userId,
        userNom,
        courrier.modeEnvoi === 'email' ? 'email' : 'postal'
      );
      const updated = await getCourrierById(courrier.id);
      setCourrier(updated);
    } catch (err) {
      console.error('Erreur envoi:', err);
      setError('Erreur lors de l\'envoi');
    } finally {
      setActionEnCours(null);
    }
  };

  /**
   * Session 47 : Génère et télécharge le PDF du courrier
   */
  const handleGenererPDF = async () => {
    if (!courrier || !copropriete) {
      setError('Impossible de générer le PDF : données manquantes');
      return;
    }

    setActionEnCours('pdf');
    try {
      // Construire l'adresse complète
      const adresseCopropriete = copropriete.adresse 
        ? `${copropriete.adresse.rue}\n${copropriete.adresse.codePostal} ${copropriete.adresse.ville}`
        : undefined;

      // Session 47 : Ajout du sceau depuis la copropriété
      await telechargerPDFCourrier({
        courrier,
        nomCopropriete: copropriete.nom,
        adresseCopropriete,
      });
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      setError('Erreur lors de la génération du PDF');
    } finally {
      setActionEnCours(null);
    }
  };

  const handleVote = async () => {
    if (!courrier || !userId) return;

    // Validation
    if (voteStatut === 'modification' && !voteCommentaire.trim()) {
      setError('Un commentaire est obligatoire pour demander une modification');
      return;
    }

    setVoteEnCours(true);
    try {
      await voterCourrier({
        courrierId: courrier.id,
        membreId: userId,
        membreNom: userNom,
        statut: voteStatut,
        commentaire: voteCommentaire.trim() || undefined
      });

      const updated = await getCourrierById(courrier.id);
      setCourrier(updated);
      setShowVoteModal(false);
      setVoteCommentaire('');
      setVoteStatut('approuve');
    } catch (err) {
      console.error('Erreur vote:', err);
      setError('Erreur lors du vote');
    } finally {
      setVoteEnCours(false);
    }
  };

  // ============================================================================
  // RENDU
  // ============================================================================

  // Formater une date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non défini';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Icône pour le type d'alerte SCUD
  const getAlerteIcon = (type: AlerteSCUD['type']) => {
    switch (type) {
      case 'terme_risque':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'accusation':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'imperatif':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'menace':
        return <ShieldAlert className="w-4 h-4 text-red-600" />;
      case 'emotion':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'juridique':
        return <Shield className="w-4 h-4 text-red-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error && !courrier) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => navigate('/admin/courriers')}
              className="mt-2 text-red-600 hover:text-red-800 underline"
            >
              Retour aux courriers
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!courrier) return null;

  const votes = compterVotes(courrier);

  return (
    <AdminLayout>
      <div className="p-6">
        {/* En-tête */}
        <div className="mb-6">
          <Link
            to="/admin/courriers"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour aux courriers
          </Link>
          
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              Courrier {courrier.numero}
            </h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUT_COURRIER_COLORS[courrier.statut]}`}>
              {STATUT_COURRIER_LABELS[courrier.statut]}
            </span>
            {/* Session 92 : Badge classification 3 niveaux */}
            {(courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification) && (() => {
              const niveau = (courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification) as NiveauClassification;
              return (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border ${NIVEAU_CLASSIFICATION_COLORS[niveau]}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[niveau]}`} />
                  {NIVEAU_CLASSIFICATION_SHORT_LABELS[niveau]}
                  {courrier.niveauSurclasse && (
                    <span title="Surclassé par le coordinateur"><ArrowUpCircle className="w-3.5 h-3.5 ml-0.5" /></span>
                  )}
                </span>
              );
            })()}
          </div>
          
          <p className="mt-1 text-sm text-gray-600">
            {TYPE_COURRIER_LABELS[courrier.type]} • {MODE_ENVOI_LABELS[courrier.modeEnvoi]} • Créé le {formatDate(courrier.dateCreation)}
          </p>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Destinataire */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                <Building2 className="w-4 h-4" />
                Destinataire
              </h2>
              <div className="space-y-2">
                <p className="text-lg font-semibold text-gray-900">
                  {courrier.destinataire.nom}
                  <span className="ml-2 text-sm font-normal text-gray-500 capitalize">
                    {courrier.destinataire.type}
                  </span>
                </p>
                {courrier.destinataire.email && (
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {courrier.destinataire.email}
                  </p>
                )}
                {courrier.destinataire.adresse && (
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {courrier.destinataire.adresse}
                  </p>
                )}
              </div>
            </div>

            {/* Contenu du courrier */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                <FileText className="w-4 h-4" />
                Contenu du courrier
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Objet</p>
                  <p className="text-base font-medium text-gray-900">{courrier.objet}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-2">Corps du message</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                    {courrier.contenu}
                  </div>
                </div>
              </div>
            </div>

            {/* Pièces jointes */}
            {courrier.piecesJointes && courrier.piecesJointes.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  <Paperclip className="w-4 h-4" />
                  Pièces jointes ({courrier.piecesJointes.length})
                </h2>
                <div className="space-y-2">
                  {courrier.piecesJointes.map((pj) => (
                    <a
                      key={pj.id}
                      href={pj.storageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{pj.nom}</p>
                        <p className="text-xs text-gray-500">
                          {(pj.taille / 1024).toFixed(1)} Ko
                        </p>
                      </div>
                      <Download className="w-4 h-4 text-gray-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Validation du CS */}
            {courrier.statut !== 'brouillon' && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  <CheckCircle className="w-4 h-4" />
                  Validation du conseil syndical
                </h2>

                {/* Session 92 : Info workflow selon le niveau */}
                {(() => {
                  const niveau = courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification;
                  if (niveau && !necessiteVoteCS(niveau)) {
                    // VERT ou ORANGE : pas de vote, validation directe
                    return (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Courrier classé <strong>{NIVEAU_CLASSIFICATION_SHORT_LABELS[niveau]}</strong> — validé directement par le coordinateur (pas de vote requis).
                          Les membres du CS ont été notifiés.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Compteur de votes (uniquement si vote requis = ROUGE ou ancien système) */}
                {(courrier.votesRequis > 0 || Object.keys(courrier.validations || {}).length > 0) && (
                  <>
                    <div className="flex gap-4 mb-4 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <ThumbsUp className="w-4 h-4" />
                        {votes.approuve} approuvé{votes.approuve > 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1 text-amber-600">
                        <PenLine className="w-4 h-4" />
                        {votes.modification} modification{votes.modification > 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1 text-red-600">
                        <ThumbsDown className="w-4 h-4" />
                        {votes.rejete} rejet{votes.rejete > 1 ? 's' : ''}
                      </span>
                      <span className="text-gray-500 ml-auto">
                        {(() => {
                          const niveau = courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification;
                          if (niveau === 'rouge') {
                            return `${votes.approuve} approbation(s) — 1 requise`;
                          }
                          return `${Object.keys(courrier.validations).length} / ${courrier.votesRequis} votes`;
                        })()}
                      </span>
                    </div>

                    {/* Liste des membres */}
                    <div className="space-y-2">
                      {membresCS.map((membre) => {
                        const vote = courrier.validations[membre.userId || ''];
                        return (
                          <div
                            key={membre.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">
                                {(membre.prenom?.[0] || '?').toUpperCase()}
                                {(membre.nom?.[0] || '?').toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {membre.prenom || 'Inconnu'} {membre.nom}
                                </p>
                                <p className="text-xs text-gray-500">Membre CS</p>
                              </div>
                            </div>
                            <div>
                              {vote ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${STATUT_VOTE_COLORS[vote.statut]}`}>
                                  {STATUT_VOTE_LABELS[vote.statut]}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <Clock className="w-3 h-3" />
                                  En attente
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Bouton voter */}
                {courrier.statut === 'en_validation' && estMembreCS && !aDejaVote && (
                  <button
                    onClick={() => setShowVoteModal(true)}
                    className="mt-4 w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Voter sur ce courrier
                  </button>
                )}
              </div>
            )}

            {/* Analyse Sentinelle */}
            {courrier.analyseSentinelle && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  <Shield className="w-4 h-4" />
                  Analyse SENTINELLE
                </h2>

                {/* Session 92 : Badge classification 3 niveaux */}
                {(courrier.niveauClassification || courrier.analyseSentinelle.niveauClassification) && (() => {
                  const niveau = (courrier.niveauClassification || courrier.analyseSentinelle.niveauClassification) as NiveauClassification;
                  return (
                    <div className="mb-4">
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold border ${NIVEAU_CLASSIFICATION_COLORS[niveau]}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[niveau]}`} />
                        {NIVEAU_CLASSIFICATION_LABELS[niveau]}
                        {courrier.niveauSurclasse && (
                          <span title="Surclassé"><ArrowUpCircle className="w-3.5 h-3.5" /></span>
                        )}
                      </span>
                      {courrier.niveauSurclasse && (
                        <p className="mt-1 text-xs text-amber-600">Surclassé manuellement par le coordinateur</p>
                      )}
                    </div>
                  );
                })()}

                <div className="mb-4">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${NIVEAU_RISQUE_COLORS[courrier.analyseSentinelle.scoreRisque]}`}>
                    {courrier.analyseSentinelle.scoreRisque === 'faible' && <ShieldCheck className="w-4 h-4" />}
                    {courrier.analyseSentinelle.scoreRisque === 'moyen' && <Shield className="w-4 h-4" />}
                    {courrier.analyseSentinelle.scoreRisque === 'eleve' && <ShieldAlert className="w-4 h-4" />}
                    {NIVEAU_RISQUE_LABELS[courrier.analyseSentinelle.scoreRisque]}
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-4">
                  {courrier.analyseSentinelle.analyseComplete}
                </p>

                {courrier.analyseSentinelle.alertes.length > 0 && (
                  <div className="space-y-2">
                    {courrier.analyseSentinelle.alertes.map((alerte, index) => (
                      <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          {getAlerteIcon(alerte.type)}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-amber-800">{alerte.message}</p>
                            {alerte.extrait && (
                              <p className="mt-1 text-xs text-amber-600 italic">"{alerte.extrait}"</p>
                            )}
                            {alerte.suggestion && (
                              <p className="mt-1 text-xs text-green-700">
                                <Lightbulb className="w-3 h-3 inline mr-1" />
                                Suggestion : {alerte.suggestion}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Colonne latérale */}
          <div className="space-y-6">
            {/* Actions - Session 92 : Visible aussi pour brouillons */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Actions
              </h2>
              <div className="space-y-2">
                {/* Brouillon : Soumettre + Modifier + Surclasser */}
                {courrier.statut === 'brouillon' && (
                  <>
                    <button
                      onClick={handleSoumettre}
                      disabled={!peutSoumettre(courrier) || analyseEnCours}
                      className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {analyseEnCours ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Analyser et soumettre
                    </button>
                    <button
                      onClick={() => navigate(`/admin/courriers/${courrier.id}/modifier`)}
                      className="w-full py-2.5 px-4 text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Modifier
                    </button>

                    {/* Session 92 : Surclassement si un niveau est déjà attribué */}
                    {(courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification) && (() => {
                      const niveauActuel = (courrier.niveauClassification || courrier.analyseSentinelle?.niveauClassification) as NiveauClassification;
                      const niveauxPossibles = getNiveauxSurclassementPossibles(niveauActuel);
                      if (niveauxPossibles.length === 0) return null;
                      return (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-2">Surclasser le niveau (par precaution) :</p>
                          <div className="space-y-1">
                            {niveauxPossibles.map((niveau) => (
                              <button
                                key={niveau}
                                onClick={async () => {
                                  const confirm = window.confirm(
                                    `Surclasser ce courrier de ${NIVEAU_CLASSIFICATION_SHORT_LABELS[niveauActuel]} vers ${NIVEAU_CLASSIFICATION_SHORT_LABELS[niveau]} ?\n\n${NIVEAU_CLASSIFICATION_DESCRIPTIONS[niveau]}\n\nCette action est irréversible.`
                                  );
                                  if (!confirm || !userId) return;
                                  try {
                                    setActionEnCours('surclasser');
                                    await surclasserNiveau(courrier.id, niveau, userId, userNom);
                                    const updated = await getCourrierById(courrier.id);
                                    setCourrier(updated);
                                  } catch (err: any) {
                                    setError(err.message || 'Erreur lors du surclassement');
                                  } finally {
                                    setActionEnCours(null);
                                  }
                                }}
                                disabled={actionEnCours === 'surclasser'}
                                className={`w-full py-2 px-3 text-sm font-medium rounded-lg border flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${NIVEAU_CLASSIFICATION_COLORS[niveau]} hover:opacity-80`}
                              >
                                <ArrowUpCircle className="w-4 h-4" />
                                Passer en {NIVEAU_CLASSIFICATION_SHORT_LABELS[niveau]}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Session 75 : Annuler visible uniquement pour Gestionnaire/Admin SaaS */}
                    {canAnnulerCourrier && (
                      <button
                        onClick={handleAnnuler}
                        disabled={actionEnCours === 'annuler'}
                        className="w-full py-2.5 px-4 text-red-600 border border-red-200 rounded-lg font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {actionEnCours === 'annuler' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        Annuler le courrier
                      </button>
                    )}
                  </>
                )}

                {/* En validation : Annuler (Gestionnaire/Admin SaaS uniquement) */}
                {courrier.statut === 'en_validation' && canAnnulerCourrier && (
                  <button
                    onClick={handleAnnuler}
                    disabled={actionEnCours === 'annuler'}
                    className="w-full py-2.5 px-4 text-red-600 border border-red-200 rounded-lg font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {actionEnCours === 'annuler' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    Annuler le courrier
                  </button>
                )}

                {/* Session 47 : Validé - Séparer Email et PDF */}
                {courrier.statut === 'valide' && (
                  <>
                    {courrier.modeEnvoi === 'email' ? (
                      // Mode email : bouton envoyer
                      <button
                        onClick={handleEnvoyer}
                        disabled={actionEnCours === 'envoyer'}
                        className="w-full py-2.5 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {actionEnCours === 'envoyer' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        Envoyer par email
                      </button>
                    ) : (
                      // Mode postal : bouton générer PDF + marquer comme envoyé
                      <>
                        <button
                          onClick={handleGenererPDF}
                          disabled={actionEnCours === 'pdf'}
                          className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {actionEnCours === 'pdf' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Printer className="w-4 h-4" />
                          )}
                          {actionEnCours === 'pdf' ? 'Génération...' : 'Générer le PDF'}
                        </button>
                        <button
                          onClick={handleEnvoyer}
                          disabled={actionEnCours === 'envoyer'}
                          className="w-full py-2.5 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {actionEnCours === 'envoyer' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          Marquer comme envoyé
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Annulé ou Rejeté: Dupliquer */}
                {['annule', 'rejete'].includes(courrier.statut) && (
                  <button
                    onClick={handleDupliquer}
                    disabled={actionEnCours === 'dupliquer'}
                    className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {actionEnCours === 'dupliquer' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    Créer nouvelle version
                  </button>
                )}

                {/* Envoyé: Aucune action mais possibilité de re-télécharger le PDF */}
                {courrier.statut === 'envoye' && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      Courrier envoyé le<br />
                      {formatDate(courrier.dateEnvoi)}
                    </p>
                    {courrier.modeEnvoi !== 'email' && (
                      <button
                        onClick={handleGenererPDF}
                        disabled={actionEnCours === 'pdf'}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto disabled:opacity-50"
                      >
                        {actionEnCours === 'pdf' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        {actionEnCours === 'pdf' ? 'Génération...' : 'Re-télécharger le PDF'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Informations */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Informations
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-gray-500">Rédigé par</p>
                    <p className="font-medium text-gray-900">{courrier.redacteurNom}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-gray-500">Dernière modification</p>
                    <p className="font-medium text-gray-900">{formatDate(courrier.dateModification)}</p>
                  </div>
                </div>
                {courrier.version > 1 && (
                  <div className="flex items-center gap-3">
                    <Copy className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Version</p>
                      <p className="font-medium text-gray-900">v{courrier.version}</p>
                    </div>
                  </div>
                )}
                {courrier.affaireId && (
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Affaire liée</p>
                      <Link
                        to={`/admin/affaires/${courrier.affaireId}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        Voir l'affaire
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modale de vote */}
        {showVoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Voter sur ce courrier</h2>
                <button
                  onClick={() => setShowVoteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Votre décision
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="vote"
                        value="approuve"
                        checked={voteStatut === 'approuve'}
                        onChange={() => setVoteStatut('approuve')}
                        className="w-4 h-4 text-green-600"
                      />
                      <ThumbsUp className="w-5 h-5 text-green-500" />
                      <span className="font-medium text-gray-900">Approuver</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="vote"
                        value="modification"
                        checked={voteStatut === 'modification'}
                        onChange={() => setVoteStatut('modification')}
                        className="w-4 h-4 text-amber-600"
                      />
                      <PenLine className="w-5 h-5 text-amber-500" />
                      <span className="font-medium text-gray-900">Demander une modification</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="vote"
                        value="rejete"
                        checked={voteStatut === 'rejete'}
                        onChange={() => setVoteStatut('rejete')}
                        className="w-4 h-4 text-red-600"
                      />
                      <ThumbsDown className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-gray-900">Rejeter</span>
                    </label>
                  </div>
                </div>

                {(voteStatut === 'modification' || voteStatut === 'rejete') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commentaire {voteStatut === 'modification' && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={voteCommentaire}
                      onChange={(e) => setVoteCommentaire(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={voteStatut === 'modification' 
                        ? "Décrivez les modifications souhaitées..." 
                        : "Motif du rejet (optionnel)..."
                      }
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setShowVoteModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleVote}
                  disabled={voteEnCours || (voteStatut === 'modification' && !voteCommentaire.trim())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {voteEnCours && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirmer mon vote
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale analyse SCUD */}
        {showAnalyseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Analyse SENTINELLE
                </h2>
                {!analyseEnCours && (
                  <button
                    onClick={() => setShowAnalyseModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="p-6">
                {analyseEnCours ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                    <p className="text-lg font-medium text-gray-900">Analyse en cours...</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Sentinelle vérifie le ton et le contenu de votre courrier
                    </p>
                  </div>
                ) : analyseResultat ? (
                  <div className="space-y-6">
                    {/* Session 92 : Niveau de classification */}
                    {analyseResultat.niveauClassification && (
                      <div className="text-center">
                        <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-lg font-bold border-2 ${NIVEAU_CLASSIFICATION_COLORS[analyseResultat.niveauClassification]}`}>
                          <span className={`w-3.5 h-3.5 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[analyseResultat.niveauClassification]}`} />
                          {NIVEAU_CLASSIFICATION_LABELS[analyseResultat.niveauClassification]}
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          {NIVEAU_CLASSIFICATION_DESCRIPTIONS[analyseResultat.niveauClassification]}
                        </p>
                      </div>
                    )}

                    {/* Score de risque (détail) */}
                    <div className="text-center">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-semibold ${NIVEAU_RISQUE_COLORS[analyseResultat.scoreRisque]}`}>
                        {analyseResultat.scoreRisque === 'faible' && <ShieldCheck className="w-6 h-6" />}
                        {analyseResultat.scoreRisque === 'moyen' && <Shield className="w-6 h-6" />}
                        {analyseResultat.scoreRisque === 'eleve' && <ShieldAlert className="w-6 h-6" />}
                        {NIVEAU_RISQUE_LABELS[analyseResultat.scoreRisque]}
                      </div>
                      <p className="mt-3 text-gray-700">{analyseResultat.analyseComplete}</p>
                    </div>

                    {/* Alertes */}
                    {analyseResultat.alertes.length > 0 && (
                      <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase mb-3">
                          <AlertTriangle className="w-4 h-4" />
                          Points d'attention ({analyseResultat.alertes.length})
                        </h3>
                        <div className="space-y-3">
                          {analyseResultat.alertes.map((alerte, index) => (
                            <div 
                              key={index} 
                              className={`p-4 rounded-lg border ${
                                alerte.type === 'accusation' || alerte.type === 'menace' || alerte.type === 'juridique'
                                  ? 'bg-red-50 border-red-200'
                                  : 'bg-amber-50 border-amber-200'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {getAlerteIcon(alerte.type)}
                                <div className="flex-1">
                                  <p className={`font-medium ${
                                    alerte.type === 'accusation' || alerte.type === 'menace' || alerte.type === 'juridique'
                                      ? 'text-red-800'
                                      : 'text-amber-800'
                                  }`}>
                                    {alerte.message}
                                  </p>
                                  {alerte.extrait && (
                                    <p className={`mt-2 text-sm italic ${
                                      alerte.type === 'accusation' || alerte.type === 'menace' || alerte.type === 'juridique'
                                        ? 'text-red-600'
                                        : 'text-amber-600'
                                    }`}>
                                      "{alerte.extrait}"
                                    </p>
                                  )}
                                  {alerte.suggestion && (
                                    <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                                      <p className="text-sm text-green-800">
                                        <Lightbulb className="w-4 h-4 inline mr-1" />
                                        <strong>Suggestion :</strong> {alerte.suggestion}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommandations */}
                    {analyseResultat.recommandations && analyseResultat.recommandations.length > 0 && (
                      <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase mb-3">
                          <Lightbulb className="w-4 h-4" />
                          Recommandations
                        </h3>
                        <ul className="space-y-2">
                          {analyseResultat.recommandations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Actions de la modale */}
              {!analyseEnCours && analyseResultat && (
                <div className="flex flex-col gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sticky bottom-0">
                  {/* Session 92 : Info workflow selon le niveau */}
                  {analyseResultat.niveauClassification && (
                    <p className="text-xs text-center text-gray-500">
                      {analyseResultat.niveauClassification === 'rouge' 
                        ? 'Ce courrier sera soumis au vote du conseil syndical (min. 1 approbation requise)'
                        : 'Ce courrier sera directement validé (les membres CS seront notifiés)'
                      }
                    </p>
                  )}
                  <div className="flex justify-end gap-3">
                    {analyseResultat.scoreRisque !== 'faible' || analyseResultat.alertes.length > 0 ? (
                      <>
                        <button
                          onClick={handleModifierCourrier}
                          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier le courrier
                        </button>
                        <button
                          onClick={handleSoumettreQuandMeme}
                          disabled={actionEnCours === 'soumettre'}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                            analyseResultat.niveauClassification === 'rouge'
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : analyseResultat.niveauClassification === 'orange'
                                ? 'bg-orange-600 text-white hover:bg-orange-700'
                                : 'bg-green-600 text-white hover:bg-green-700'
                          } disabled:opacity-50`}
                        >
                          {actionEnCours === 'soumettre' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          {analyseResultat.niveauClassification === 'rouge'
                            ? 'Soumettre au vote CS'
                            : 'Valider et envoyer'
                          }
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setShowAnalyseModal(false)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {analyseResultat.niveauClassification === 'rouge'
                          ? 'Courrier soumis au vote !'
                          : 'Parfait, courrier validé !'
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CourrierDetailPage;
